/**
 * W-8BEN-E Form Hook
 * Custom hook for managing W-8BEN-E form state using model fields as single source of truth
 */

import { useState, useEffect, useCallback } from "react";
import { W8BENEFormModel } from "../Models/W8BENEModels";
import { W8BENEService } from "../services/W8BENEService";

export interface UseW8BENEFormOptions {
  initialData?: any;
}

export interface UseW8BENEFormReturn {
  model: W8BENEFormModel;
  isLoading: boolean;
  isSaving: boolean;
  errors: Record<string, string>;
  isValid: boolean;
  updateField: (field: string, value: string | boolean) => void;
  updateMultipleFields: (fields: Record<string, string | boolean>) => void;
  validate: () => boolean;
}

export const useW8BENEForm = (options: UseW8BENEFormOptions = {}): UseW8BENEFormReturn => {
  const { initialData } = options;

  // State management using model as single source of truth
  const [model, setModel] = useState<W8BENEFormModel>(() => {
    if (initialData) {
      // Check if this is already a mapped form to prevent re-initialization
      if (initialData.isMapDone) {
        console.log("W8BEN-E form already mapped, preserving existing state...");
        return new W8BENEFormModel(initialData);
      }
      return W8BENEService.fromApiResponse(initialData);
    }
    return new W8BENEFormModel();
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValid, setIsValid] = useState(false);

  // Update validation state when model changes
  useEffect(() => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
  }, [model]);

  /**
   * Update a single field in the model
   */
  const updateField = useCallback((field: string, value: string | boolean) => {
    setModel((prevModel) => {
      // Mark as mapped to prevent API responses from overriding user interactions
      if (!prevModel.isMapDone) {
        console.log("W8BEN-E form: Setting isMapDone=true to protect user interactions...");
      }

      const newModel = new W8BENEFormModel(prevModel);
      (newModel as any)[field] = value;
      newModel.isMapDone = true; // Protect against API overwrites
      return newModel;
    });
  }, []);

  /**
   * Update multiple fields in the model
   */
  const updateMultipleFields = useCallback((fields: Record<string, string | boolean>) => {
    console.log("🔧 updateMultipleFields called with:", fields);
    setModel((prevModel) => {
      // Mark as mapped to prevent API responses from overriding user interactions
      if (!prevModel.isMapDone) {
        console.log("W8BEN-E form: Setting isMapDone=true to protect user interactions...");
      }

      console.log(
        "🔧 Before update - prevModel field values:",
        Object.keys(fields).reduce((acc, field) => {
          acc[field] = (prevModel as any)[field];
          return acc;
        }, {} as any)
      );

      const newModel = new W8BENEFormModel(prevModel);

      console.log(
        "🔧 After constructor - newModel field values:",
        Object.keys(fields).reduce((acc, field) => {
          acc[field] = (newModel as any)[field];
          return acc;
        }, {} as any)
      );

      Object.entries(fields).forEach(([field, value]) => {
        console.log(`🔧 Setting field ${field} from ${(newModel as any)[field]} to ${value}`);
        (newModel as any)[field] = value;
        console.log(`🔧 After setting: ${field} = ${(newModel as any)[field]}`);
      });

      newModel.isMapDone = true; // Protect against API overwrites

      console.log(
        "🔧 Final newModel field values:",
        Object.keys(fields).reduce((acc, field) => {
          acc[field] = (newModel as any)[field];
          return acc;
        }, {} as any)
      );

      return newModel;
    });
  }, []);

  /**
   * Validate the current model
   */
  const validate = useCallback((): boolean => {
    const validationResult = model.validate();
    setErrors(validationResult.fieldErrors);
    setIsValid(validationResult.isValid);
    return validationResult.isValid;
  }, [model]);

  return {
    model,
    isLoading,
    isSaving,
    errors,
    isValid,
    updateField,
    updateMultipleFields,
    validate,
  };
};
